import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Pencil, Plus, Search, Star, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { adminDelete, adminList, formatApiError } from "../../lib/api";
import { KINDS } from "./adminConfig";

export default function AdminList() {
  const { kind } = useParams();
  const cfg = KINDS[kind];
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    setItems(null);
    setQuery("");
    adminList(cfg.api, cfg.type ? { type: cfg.type } : {})
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)));
  }, [kind, cfg]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return (items || []).filter((i) => (i[cfg?.titleKey] || "").toLowerCase().includes(q));
  }, [items, query, cfg]);

  if (!cfg) return <Navigate to="/admin/reviews" replace />;

  const confirmDelete = async () => {
    try {
      await adminDelete(cfg.api, pending.id);
      setItems((list) => list.filter((i) => i.id !== pending.id));
      toast.success(`Deleted "${pending[cfg.titleKey]}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setPending(null);
  };

  return (
    <div className="max-w-6xl" data-testid={`admin-list-${kind}`}>
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-8">
        <div>
          <p className="eyebrow mb-2">Content</p>
          <h1 className="font-playfair text-4xl text-neutral-900 tracking-tight">{cfg.label}</h1>
          <p className="text-sm text-neutral-500 mt-2" data-testid="admin-list-count">
            {items ? `${items.length} ${items.length === 1 ? cfg.singular : cfg.label.toLowerCase()}` : "Loading…"}
          </p>
        </div>
        <Link to={`/admin/${kind}/new`} data-testid="admin-new-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 transition-colors self-start sm:self-auto">
          <Plus size={15} /> New {cfg.singular}
        </Link>
      </header>

      <div className="relative mb-5 max-w-sm">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${cfg.label.toLowerCase()}…`} data-testid="admin-list-search" className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-full bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
      </div>

      <div className="bg-surface border border-neutral-200 rounded-xl overflow-hidden">
        <ul className="divide-y divide-neutral-200">
          {filtered.map((item) => (
            <ListRow key={item.id} item={item} cfg={cfg} kind={kind} onDelete={() => setPending(item)} />
          ))}
        </ul>
        {items && !filtered.length && (
          <p className="px-6 py-12 text-sm text-neutral-500" data-testid="admin-list-empty">Nothing here yet.</p>
        )}
      </div>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-playfair text-2xl">Delete this {cfg.singular}?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pending?.[cfg.titleKey]}" will be removed from the site permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-700 hover:bg-red-800" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ListRow({ item, cfg, kind, onDelete }) {
  const title = item[cfg.titleKey];
  const meta = [item.category, item.publication_date || item.updated_date].filter(Boolean).join(" · ");
  const iconBtn = "p-2 rounded-md text-neutral-500 hover:text-indigo-800 hover:bg-indigo-50 transition-colors";
  return (
    <li className="flex items-center gap-4 px-6 py-4 hover:bg-neutral-50/70 transition-colors" data-testid={`admin-row-${item.slug}`}>
      <div className="flex-1 min-w-0">
        <Link to={`/admin/${kind}/${item.id}/edit`} className="font-playfair text-lg text-neutral-900 hover:text-indigo-900 transition-colors line-clamp-1">
          {title}
        </Link>
        <p className="text-xs text-neutral-500 mt-1 truncate">/{item.slug}{meta && ` · ${meta}`}</p>
      </div>
      {item.status === "draft" && (
        <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-50 px-2.5 py-1 rounded-full" data-testid={`admin-draft-${item.slug}`}>Draft</span>
      )}
      {item.noindex && (
        <span className="hidden sm:inline-flex text-[11px] font-semibold uppercase tracking-wider text-neutral-600 bg-neutral-100 px-2.5 py-1 rounded-full" data-testid={`admin-noindex-${item.slug}`}>Noindex</span>
      )}
      {item.featured && (
        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-full">
          <Star size={11} /> Featured
        </span>
      )}
      <div className="flex items-center gap-1">
        {cfg.publicPath && (
          <a href={`${cfg.publicPath}/${item.slug}`} target="_blank" rel="noreferrer" className={iconBtn} aria-label="View live" data-testid={`admin-view-${item.slug}`}>
            <ExternalLink size={15} />
          </a>
        )}
        <Link to={`/admin/${kind}/${item.id}/edit`} className={iconBtn} aria-label="Edit" data-testid={`admin-edit-${item.slug}`}>
          <Pencil size={15} />
        </Link>
        <button type="button" onClick={onDelete} className={`${iconBtn} hover:text-red-700 hover:bg-red-50`} aria-label="Delete" data-testid={`admin-delete-${item.slug}`}>
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}
